import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import { useAuth } from '@/hooks/useAuth';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';

interface Domain {
  id: string;
  name: string;
}

interface ProblemStatement {
  id: string;
  title: string;
  description: string | null;
  domain_id: string | null;
  difficulty: string | null;
  is_published: boolean;
  is_enabled: boolean;
  created_at: string;
  domain_name?: string;
}

const difficultyColor = (d: string) => {
  switch (d) {
    case 'Hard': return 'bg-red-100 text-red-700';
    case 'Medium': return 'bg-amber-100 text-amber-700';
    case 'Easy': return 'bg-accent-100 text-accent-700';
    default: return 'bg-background-200 text-foreground-500';
  }
};

export default function ProblemStatementsPage() {
  const { profile } = useAuth();
  const [statements, setStatements] = useState<ProblemStatement[]>([]);
  const [teamCounts, setTeamCounts] = useState<Record<string, number>>();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPS, setNewPS] = useState({ title: '', domain_id: '', difficulty: 'Medium', description: '', doctor_name: '', hospital_name: '' });
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [psRes, domRes, teamsRes] = await Promise.all([
        supabase.from('problem_statements').select('*').order('created_at', { ascending: false }),
        supabase.from('domains').select('id, name').order('name'),
        supabase.from('teams').select('problem_statement_id').not('problem_statement_id', 'is', null),
      ]);

      if (psRes.error) throw psRes.error;
      if (domRes.error) throw domRes.error;

      const domainsMap: Record<string, string> = {};
      (domRes.data || []).forEach((d) => { domainsMap[d.id] = d.name; });

      const counts: Record<string, number> = {};
      (teamsRes.data || []).forEach((t) => {
        if (t.problem_statement_id) counts[t.problem_statement_id] = (counts[t.problem_statement_id] || 0) + 1;
      });

      const enriched = (psRes.data || []).map((ps) => ({
        ...ps,
        domain_name: ps.domain_id ? (domainsMap[ps.domain_id] || 'Unknown') : 'Unassigned',
      }));

      setStatements(enriched);
      setTeamCounts(counts);
      setDomains(domRes.data || []);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load problem statements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useRealtime<ProblemStatement>({
    table: 'problem_statements',
    onAny: () => {
      loadData();
    },
  });

  const filtered = statements.filter((ps) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return ps.title.toLowerCase().includes(q) || (ps.domain_name || '').toLowerCase().includes(q);
  });

  const togglePublish = async (id: string, current: boolean) => {
    try {
      const { error: err } = await supabase.from('problem_statements').update({ is_published: !current }).eq('id', id);
      if (err) throw err;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const toggleEnable = async (id: string, current: boolean) => {
    try {
      const { error: err } = await supabase.from('problem_statements').update({ is_enabled: !current }).eq('id', id);
      if (err) throw err;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: err } = await supabase.from('problem_statements').delete().eq('id', id);
      if (err) throw err;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleAddOrEdit = async () => {
    if (!newPS.title.trim() || !newPS.domain_id) return;
    setSaving(true);
    setError('');
    try {
      if (editId) {
        const { error: err } = await supabase.from('problem_statements').update({
          title: newPS.title.trim(),
          domain_id: newPS.domain_id,
          difficulty: newPS.difficulty,
          description: newPS.description.trim(),
          doctor_name: newPS.doctor_name.trim(),
          hospital_name: newPS.hospital_name.trim(),
          updated_at: new Date().toISOString(),
        }).eq('id', editId);
        if (err) throw err;
      } else {
        const { error: insertErr } = await supabase.from('problem_statements').insert({
          title: newPS.title.trim(),
          domain_id: newPS.domain_id,
          difficulty: newPS.difficulty,
          description: newPS.description.trim(),
          doctor_name: newPS.doctor_name.trim(),
          hospital_name: newPS.hospital_name.trim(),
          is_published: false,
          is_enabled: true,
          created_by: profile?.id,
        }).select();

        if (insertErr) {
          console.error('[ProblemStatements] Insert error:', insertErr);
          throw new Error(insertErr.message || 'Insert failed — check RLS policies or domain_id validity');
        }

        const { error: auditErr } = await supabase.from('audit_logs').insert({
          user_id: profile?.id,
          user_name: profile?.full_name || 'Admin',
          role: profile?.role,
          action: 'Problem Statement Created',
          module: 'Problem Statements',
        });

        if (auditErr) {
          // Non-fatal: just log audit failure
          console.warn('[ProblemStatements] Audit log failed:', auditErr.message);
        }
      }
      setShowAddModal(false);
      setEditId(null);
      setNewPS({ title: '', domain_id: '', difficulty: 'Medium', description: '', doctor_name: '', hospital_name: '' });
      // Force reload
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (ps: ProblemStatement) => {
    setEditId(ps.id);
    setNewPS({
      title: ps.title,
      domain_id: ps.domain_id || '',
      difficulty: ps.difficulty || 'Medium',
      description: ps.description || '',
      doctor_name: (ps as Record<string, unknown>).doctor_name as string || '',
      hospital_name: (ps as Record<string, unknown>).hospital_name as string || '',
    });
    setShowAddModal(true);
  };

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="Problem Statements" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Problem Statements' }]}>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="cursor-pointer"><i className="ri-close-line"></i></button>
          </div>
        )}

        <div className="flex items-center justify-between mb-5">
          <div className="relative w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search problem statements..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
          </div>
          <button onClick={() => { setEditId(null); setNewPS({ title: '', domain_id: '', difficulty: 'Medium', description: '', doctor_name: '', hospital_name: '' }); setShowAddModal(true); }} className="px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line"></i>
            Add Problem Statement
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
          </div>
        ) : statements.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-question-answer-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">No problem statements available</p>
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    {['Title', 'Domain', 'Difficulty', 'Teams', 'Status', 'Published', 'Date', 'Actions'].map((label) => (
                      <th key={label} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((ps) => (
                    <tr key={ps.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-900 max-w-xs truncate">{ps.title}</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap text-xs">{ps.domain_name}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${difficultyColor(ps.difficulty || '')}`}>{ps.difficulty || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-background-100 text-xs font-medium text-foreground-600">{teamCounts[ps.id] || 0}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => toggleEnable(ps.id, ps.is_enabled)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${ps.is_enabled ? 'bg-accent-500' : 'bg-background-300'}`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${ps.is_enabled ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => togglePublish(ps.id, ps.is_published)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${ps.is_published ? 'bg-primary-500' : 'bg-background-300'}`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${ps.is_published ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs">{new Date(ps.created_at).toLocaleDateString('en-IN')}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(ps)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Edit">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button onClick={() => handleDelete(ps.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer" title="Delete">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-foreground-400">No problem statements found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowAddModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-background-200/70 p-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{editId ? 'Edit Problem Statement' : 'Add Problem Statement'}</h3>
                <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Problem Statement Title</label>
                  <input type="text" value={newPS.title} onChange={(e) => setNewPS({ ...newPS, title: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="Enter problem statement title..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Domain</label>
                  <select value={newPS.domain_id} onChange={(e) => setNewPS({ ...newPS, domain_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                    <option value="">Select a domain...</option>
                    {domains.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Difficulty</label>
                  <select value={newPS.difficulty} onChange={(e) => setNewPS({ ...newPS, difficulty: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Description</label>
                  <textarea value={newPS.description} onChange={(e) => setNewPS({ ...newPS, description: e.target.value })} rows={4} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none resize-none" placeholder="Enter problem statement description..."></textarea>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Doctor Name</label>
                    <input type="text" value={newPS.doctor_name} onChange={(e) => setNewPS({ ...newPS, doctor_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="e.g. Dr. Ramesh Kumar" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">Hospital Name</label>
                    <input type="text" value={newPS.hospital_name} onChange={(e) => setNewPS({ ...newPS, hospital_name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="e.g. ABC Multispeciality Hospital" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowAddModal(false)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleAddOrEdit} disabled={saving} className="px-5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
                    {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Statement'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </AuthGuard>
  );
}